import { TextUtils } from "@/utils/TextUtils";
import { useEditor, useNode } from "@craftjs/core";
import clsx from "clsx";
import { useEffect, useRef } from "react";
import ContentEditable from "react-contenteditable";
import InternalLinkParser from "../../../shared/InternalLinkParser";
import Settings from "./Settings";
import Toolbar from "./Toolbar";

interface PageHeadingProps {
    text: string;
    anchorTag?: string;
    level?: number;
}

const PageHeading = ({ text = "", level = 2, anchorTag: anchorBadge = "" }: Partial<PageHeadingProps>) => {
    const editableRef = useRef<HTMLDivElement | null>(null);

    const {
        connectors: { connect },
        actions: { setProp },
    } = useNode((node) => ({
        parentNodeId: node.data.parent,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled,
    }));

    useEffect(() => {
        // Fix for nested divs being editable when they shouldn't exist due to a previous bug with pasting content.
        if (!editableRef.current) return;

        const editableDivs = editableRef.current.querySelectorAll("div[contenteditable='true']");

        for (let i = 0; i < editableDivs.length; i++) {
            const editableDiv = editableDivs[i] as HTMLDivElement;
            editableDiv.contentEditable = enabled.toString();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableRef.current, enabled]);

    useEffect(() => {
        // Fix for pasted content sometimes having font-size attribute.
        if (!editableRef.current) return;

        const elementsWithFontSize = editableRef.current.querySelectorAll("[style*='font-size']");

        for (let i = 0; i < elementsWithFontSize.length; i++) {
            const element = elementsWithFontSize[i] as HTMLElement;
            element.style.fontSize = "";
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableRef.current, enabled]);

    const plainText = TextUtils.getPlainText(text || "");

    if (!enabled)
        return (
            <div ref={(ref) => connect(ref!)} className="craftjs-heading w-full">
                {level === 2 && (
                    <span
                        className="craftjs-heading-anchor"
                        data-anchor-label={plainText}
                        data-anchor-tag={anchorBadge}
                    />
                )}

                <InternalLinkParser
                    as={`h${level}` as "h2" | "h3" | "h4"}
                    html={text}
                    className={clsx([
                        "font-bold",
                        level === 2 && "text-[45px]/[64px]",
                        level === 3 && "text-[36px]",
                        level === 4 && "text-[30px]",
                    ])}
                />
            </div>
        );

    return (
        <div
            ref={(ref) => connect(ref!)}
            className="craftjs-heading w-full"
            style={{
                minHeight: enabled ? (text ? "unset" : "1rem") : "unset",
            }}
        >
            {level === 2 && (
                <span className="craftjs-heading-anchor" data-anchor-label={plainText} data-anchor-tag={anchorBadge} />
            )}

            <ContentEditable
                html={text || ""}
                innerRef={editableRef}
                disabled={!enabled}
                onChange={(e) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    setProp((props: any) => (props.text = e.target.value), 500);
                }}
                onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData("text/plain");
                    document.execCommand("insertText", false, text);
                }}
                tagName={`h${level}`}
                className={clsx([
                    "font-bold",
                    level === 2 && "text-[45px]/[64px]",
                    level === 3 && "text-[36px]",
                    level === 4 && "text-[30px]",
                ])}
            />
        </div>
    );
};

export default PageHeading;

PageHeading.craft = {
    displayName: "Heading",
    props: {
        text: "Text",
        anchorTag: "",
        level: 2,
    },
    related: {
        settings: Settings,
        toolbar: Toolbar,
    },
    rules: {
        canDrag: () => true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        canDrop: (targetNode: any) =>
            targetNode.data.name === "PageContentContainer" ||
            targetNode.data.name === "Div" ||
            targetNode.data.name === "Grid",
    },
};
